import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  formatMoney,
  italianToday,
  type TransferAutoMatchResult,
  type TransferListItem,
  type TransfersResponse,
  type TransferState,
} from '@k-management/shared';
import { useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { useCan } from '../../auth/AuthProvider.js';
import { usePageTitle } from '../../components/layout/page-chrome.js';
import { apiRequest } from '../../lib/api.js';
import { formatDate } from '../../lib/format.js';
import { announceTreasuryChange, movementPath } from './treasury-ui.js';

const STATE_LABELS: Record<TransferState, { label: string; tone: string }> = {
  abbinato: { label: 'abbinato', tone: 'ok' },
  'da-abbinare': { label: 'manca il movimento dell’altro conto', tone: 'bad' },
  'conto-da-indicare': { label: 'altro conto da indicare', tone: 'warn' },
};

function OtherSide({ item }: { item: TransferListItem }) {
  const other = item.other;
  if (!other?.accountCode) return <span className="muted">?</span>;
  return other.movementId && other.bookingDate ? (
    <Link to={movementPath(other.accountCode, other.movementId)}>
      {other.accountAlias} {formatDate(other.bookingDate)}
      {other.created && <small className="muted"> (creato)</small>}
    </Link>
  ) : (
    <>{other.accountAlias} —</>
  );
}

/**
 * Tesoreria › Giroconti (3.13): the transfers between our accounts and the
 * withdrawals of a year, a pair once, with those still without their other
 * movement; «Abbina da soli» matches where there is one candidate only.
 */
export function TransfersPage() {
  usePageTitle('Giroconti');
  const queryClient = useQueryClient();
  const canWrite = useCan('tesoreria.write');
  const [params, setParams] = useSearchParams();
  const current = Number(italianToday().slice(0, 4));
  const year = Number(params.get('anno') ?? current);
  const onlyOpen = params.get('stato') === 'aperti';
  const list = useQuery({
    queryKey: ['treasury', 'transfers', year],
    queryFn: () => apiRequest<TransfersResponse>(`/treasury/transfers?year=${year}`),
  });
  const [result, setResult] = useState<TransferAutoMatchResult | null>(null);
  const match = useMutation({
    mutationFn: () =>
      apiRequest<TransferAutoMatchResult>('/treasury/transfers/auto-match', { method: 'POST' }),
    onSuccess: async (data) => {
      setResult(data);
      announceTreasuryChange();
      await queryClient.invalidateQueries({ queryKey: ['treasury'] });
    },
  });
  const items = (list.data?.items ?? []).filter((item) => !onlyOpen || item.state !== 'abbinato');
  const open = (list.data?.items ?? []).filter((item) => item.state !== 'abbinato').length;
  const years = Array.from({ length: current - 2022 }, (_, index) => current - index);

  return (
    <section className="stack">
      <p className="muted small">
        Un giroconto è un’uscita da un conto e un’entrata in un altro entro 4 giorni; un prelievo va
        sempre in Cassa. Si sistema quando è abbinato al movimento dell’altro conto: fino ad allora
        resta in Da sistemare e la chiusura dell’anno non passa.
      </p>
      <div className="row between">
        <div className="row">
          <label>
            Anno{' '}
            <select
              value={year}
              onChange={(event) =>
                setParams({ anno: event.target.value, stato: params.get('stato') ?? '' })
              }
            >
              {years.map((item) => (
                <option key={item} value={item}>
                  {item}
                </option>
              ))}
            </select>
          </label>
          <label className="checkbox">
            <input
              type="checkbox"
              checked={onlyOpen}
              onChange={(event) =>
                setParams({ anno: String(year), stato: event.target.checked ? 'aperti' : '' })
              }
            />{' '}
            Solo da abbinare ({open})
          </label>
        </div>
        {canWrite && (
          <button
            type="button"
            className="primary"
            disabled={match.isPending}
            onClick={() => match.mutate()}
          >
            Abbina da soli
          </button>
        )}
      </div>
      {match.error && <p className="error">{match.error.message}</p>}
      {result && (
        <p className="card small">
          Abbinati {result.paired}, movimenti creati in Cassa {result.created}; restano da abbinare
          a mano {result.left}
          {result.skipped > 0 &&
            `, saltati ${result.skipped} (anno chiuso o cambiati nel frattempo)`}
          . Tutti gli anni.
        </p>
      )}
      {list.error && <p className="error">{list.error.message}</p>}
      {list.data && (
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>Data</th>
                <th>Conto</th>
                <th>Descrizione</th>
                <th className="numeric">Importo</th>
                <th>Altro conto</th>
                <th>Stato</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.movementId}>
                  <td>{formatDate(item.bookingDate)}</td>
                  <td>
                    <Link to={movementPath(item.accountCode, item.movementId)}>
                      {item.accountAlias}
                    </Link>
                    {item.reason === 'prelievo' && <small className="muted block">prelievo</small>}
                  </td>
                  <td className="small">{item.description}</td>
                  <td className="numeric">{formatMoney(item.amount)}</td>
                  <td>
                    <OtherSide item={item} />
                  </td>
                  <td>
                    <span className={`pill ${STATE_LABELS[item.state].tone}`}>
                      {STATE_LABELS[item.state].label}
                    </span>
                  </td>
                </tr>
              ))}
              {items.length === 0 && (
                <tr>
                  <td colSpan={6} className="muted">
                    Nessun giroconto {onlyOpen ? 'da abbinare ' : ''}nel {year}.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
